import { useMemo, useState, useEffect, useRef } from 'react'
import type { Image } from '../types/image'

const MIN_COLUMN_WIDTH = 250

export function useMasonry(
  images: Image[],
  containerEl: HTMLDivElement | null
): { columns: Image[][]; columnWidth: number; colCount: number } {
  const [containerWidth, setContainerWidth] = useState(0)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!containerEl) return

    const observer = new ResizeObserver(([entry]) => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
      }
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        setContainerWidth(entry.contentRect.width)
      })
    })
    observer.observe(containerEl)

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      observer.disconnect()
    }
  }, [containerEl])

  return useMemo(() => {
    if (containerWidth === 0 || images.length === 0) {
      return { columns: [], columnWidth: 0, colCount: 1 }
    }

    const colCount = Math.max(1, Math.floor(containerWidth / MIN_COLUMN_WIDTH))
    const colWidth = Math.floor(containerWidth / colCount)

    const columns: Image[][] = Array.from({ length: colCount }, () => [])
    const colHeights: number[] = new Array(colCount).fill(0)

    for (const img of images) {
      const aspectRatio = img.width && img.height ? img.width / img.height : 1
      const estimatedHeight = colWidth / aspectRatio

      let shortestCol = 0
      for (let c = 1; c < colCount; c++) {
        if (colHeights[c] < colHeights[shortestCol]) {
          shortestCol = c
        }
      }

      columns[shortestCol].push(img)
      colHeights[shortestCol] += estimatedHeight
    }

    return { columns, columnWidth: colWidth, colCount }
  }, [images, containerWidth])
}
